import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  ProductsGeneralFlatTeamSettingGroupById,
  type ProductsGeneralFlatTeamSettingGroupId,
} from "./groups";

type ProductsGeneralSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: ProductsGeneralSettingInput,
): ProductsGeneralSettingInput {
  return metadata;
}

function productsGeneralGroup<
  const Group extends ProductsGeneralFlatTeamSettingGroupId,
  const T extends Record<string, ProductsGeneralSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const ProductsGeneralRegistry = {
  ...productsGeneralGroup("products_general.hazmat", {
    "products/hazmat/lithiumBattery/tagsInclude": setting({
      label: "Lithium battery tags",
      description:
        "Products with these Shopify tags are treated as containing lithium batteries and flagged as hazmat.",
      synonyms: [
        "lithium battery tags",
        "hazmat tags",
        "hazardous material tags",
        "flag products as hazmat",
        "hazardous materials",
        "mark products as hazmat",
      ],
    }),
  }),
};

type ProductsGeneralFlatTeamSettingKey = keyof typeof ProductsGeneralRegistry;
const ProductsGeneralFlatTeamSettingKeys = Object.keys(
  ProductsGeneralRegistry,
) as ProductsGeneralFlatTeamSettingKey[];

export const ProductsGeneralFlatTeamSettingCatalogDefinitions =
  ProductsGeneralFlatTeamSettingKeys.map((key) => {
    const definition = ProductsGeneralRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: ProductsGeneralFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    ProductsGeneralFlatTeamSettingKey,
    ProductsGeneralFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<ProductsGeneralFlatTeamSettingKey>
  >[];

export const ProductsGeneralFlatTeamSettingMetadataByKey = Object.fromEntries(
  ProductsGeneralFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<ProductsGeneralFlatTeamSettingKey, SettingMetadata>;
